import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy, Flame, Zap, BookOpen, Medal, Users, Copy, LogOut, Plus, UserPlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AdBanner, { getAdsConfig } from "@/components/AdBanner";

const MEDAL = ["#FFD830", "#C0C0C0", "#CD7F32"];

export default function Leaderboard() {
  const [entries, setEntries] = useState([]);
  const [hasSideAds, setHasSideAds] = useState(false);
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [newTeamName, setNewTeamName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);

  const loadEntries = () => {
    api.get("/leaderboard").then((r) => setEntries(r.data.entries)).catch(() => {});
  };

  const loadTeams = () => {
    setLoadingTeams(true);
    api.get("/teams/mine").then((r) => setTeams(r.data.teams)).catch(() => {}).finally(() => setLoadingTeams(false));
  };

  useEffect(() => {
    loadEntries();
    loadTeams();
  }, []);

  useEffect(() => {
    getAdsConfig().then((cfg) => {
      setHasSideAds(!!(cfg?.enabled && (cfg.leaderboard_left_snippet || cfg.leaderboard_right_snippet)));
    });
  }, []);

  const createTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/teams/create", { name: newTeamName.trim() });
      toast.success(`Team "${data.name}" erstellt! Code: ${data.code}`);
      setNewTeamName("");
      loadTeams();
      loadEntries();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const joinTeam = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/teams/join", { code: joinCode.trim() });
      toast.success(`Team "${data.name}" beigetreten! 🎉`);
      setJoinCode("");
      loadTeams();
      loadEntries();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setJoining(false);
    }
  };

  const leaveTeam = async (team) => {
    if (!confirm(`Team "${team.name}" wirklich verlassen?`)) return;
    try {
      await api.post(`/teams/${team.id}/leave`);
      toast.success(`Team "${team.name}" verlassen.`);
      loadTeams();
      loadEntries();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard?.writeText(code);
    toast.success(`Code "${code}" kopiert`);
  };

  const content = (
    <div className="space-y-6" data-testid="leaderboard-page">
      <div className="animate-fade-up">
        <div className="flex items-center gap-2">
          <Trophy className="h-7 w-7 text-primary" strokeWidth={2.5} />
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Bestenliste</h1>
        </div>
        <p className="text-muted-foreground font-medium mt-1">Du siehst hier nur dich und deine Team-Mitglieder.</p>
      </div>

      {/* Team management */}
      <div className="nb-card p-5" data-testid="teams-panel">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-secondary" strokeWidth={2.5} />
          <h2 className="font-heading font-black text-lg">Meine Teams</h2>
        </div>

        {!loadingTeams && teams.length > 0 && (
          <div className="space-y-2 mb-4">
            {teams.map((t) => (
              <div key={t.id} data-testid={`team-row-${t.id}`} className="flex items-center gap-3 px-3 py-2 rounded-lg border-2 border-black bg-white">
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">{t.name}</div>
                  <div className="text-[11px] text-muted-foreground font-medium">{t.member_count} Mitglied{t.member_count !== 1 ? "er" : ""}</div>
                </div>
                <button
                  onClick={() => copyCode(t.code)}
                  data-testid={`team-copy-${t.id}`}
                  className="flex items-center gap-1 text-xs font-bold px-2 py-1 border-2 border-black rounded-md bg-accent nb-press"
                  title="Code kopieren"
                >
                  <Copy className="h-3 w-3" strokeWidth={2.5} /> {t.code}
                </button>
                <button
                  onClick={() => leaveTeam(t)}
                  data-testid={`team-leave-${t.id}`}
                  className="p-1.5 border-2 border-transparent hover:border-black hover:bg-destructive hover:text-white rounded-md"
                  title="Team verlassen"
                >
                  <LogOut className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        )}
        {!loadingTeams && teams.length === 0 && (
          <p className="text-sm text-muted-foreground font-medium mb-4">
            Du bist noch in keinem Team. Erstelle eins oder tritt mit einem Code bei – dann siehst du hier eure gemeinsame Rangliste.
          </p>
        )}

        <div className="grid sm:grid-cols-2 gap-3">
          <form onSubmit={createTeam} className="flex gap-2">
            <input
              type="text"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              placeholder="Team-Name"
              data-testid="team-create-name"
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm font-bold min-w-0"
            />
            <button
              type="submit"
              disabled={creating}
              data-testid="team-create-button"
              className="flex items-center gap-1 bg-primary text-white font-bold text-sm px-3 py-2 rounded-lg border-2 border-black nb-shadow-sm nb-press disabled:opacity-60 shrink-0"
            >
              <Plus className="h-4 w-4" strokeWidth={2.5} /> Erstellen
            </button>
          </form>
          <form onSubmit={joinTeam} className="flex gap-2">
            <input
              type="text"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Team-Code"
              data-testid="team-join-code"
              className="flex-1 px-3 py-2 border-2 border-black rounded-lg text-sm font-bold uppercase min-w-0"
            />
            <button
              type="submit"
              disabled={joining}
              data-testid="team-join-button"
              className="flex items-center gap-1 bg-secondary text-white font-bold text-sm px-3 py-2 rounded-lg border-2 border-black nb-shadow-sm nb-press disabled:opacity-60 shrink-0"
            >
              <UserPlus className="h-4 w-4" strokeWidth={2.5} /> Beitreten
            </button>
          </form>
        </div>
      </div>

      <div className="space-y-2">
        {entries.map((e) => (
          <div
            key={e.id}
            data-testid={`leaderboard-row-${e.rank}`}
            className={`nb-card p-4 flex items-center gap-4 ${e.is_me ? "bg-secondary text-white" : "bg-white"}`}
          >
            <div className="w-10 flex justify-center">
              {e.rank <= 3 ? (
                <div
                  className="h-9 w-9 rounded-full border-2 border-black flex items-center justify-center nb-shadow-sm"
                  style={{ backgroundColor: MEDAL[e.rank - 1] }}
                >
                  <Medal className="h-5 w-5 text-black" strokeWidth={2.5} />
                </div>
              ) : (
                <span className="font-heading font-black text-xl text-muted-foreground">{e.rank}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">
                {e.name} {e.is_me && <span className="text-xs">(Du)</span>}
              </div>
              <div className={`flex items-center gap-3 text-xs font-medium ${e.is_me ? "text-white/80" : "text-muted-foreground"}`}>
                <span className="flex items-center gap-1"><Flame className="h-3.5 w-3.5" strokeWidth={2.5} /> {e.streak}</span>
                <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" strokeWidth={2.5} /> {e.lessons}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 font-heading font-black text-xl">
              <Zap className={`h-5 w-5 ${e.is_me ? "text-warning" : "text-accent"}`} strokeWidth={2.5} />
              {e.xp}
            </div>
          </div>
        ))}
        {entries.length === 0 && (
          <p className="text-muted-foreground font-medium">Noch keine Einträge.</p>
        )}
      </div>
    </div>
  );

  if (!hasSideAds) return content;

  return (
    <div className="grid lg:grid-cols-[220px_1fr_220px] gap-6 items-start">
      <div className="hidden lg:block lg:sticky lg:top-20">
        <AdBanner field="leaderboard_left_snippet" className="w-full" />
      </div>
      {content}
      <div className="hidden lg:block lg:sticky lg:top-20">
        <AdBanner field="leaderboard_right_snippet" className="w-full" />
      </div>
    </div>
  );
}
